import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Patient } from '@/types';
import { useCreatePatient, useUpdatePatient } from '@/hooks/usePatients';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import Select from '@/components/ui/Select';

const patientSchema = z.object({
  name: z.string().min(1, 'El nombre es requerido'),
  species: z.string().min(1, 'La especie es requerida'),
  breed: z.string().optional(),
  color: z.string().optional(),
  sex: z.enum(['male', 'female', 'unknown']),
  size: z.enum(['xs', 'small', 'medium', 'large', 'xl']).optional(),
  age_years: z.union([z.string(), z.number()]).optional(),
  age_months: z.union([z.string(), z.number()]).optional(),
  weight: z.union([z.string(), z.number()]).optional(),
  microchip_number: z.string().optional(),
  is_neutered: z.enum(['yes', 'no', 'unknown']).optional(),
  deceased: z.boolean().optional(),
  deceased_date: z.string().optional(),
  notes: z.string().optional(),
  allergies: z.string().optional(),
});

type PatientFormData = z.infer<typeof patientSchema>;

interface PatientFormProps {
  patient?: Patient | null;
  ownerId: number;
  onSuccess: () => void;
}

const speciesOptions = [
  { value: 'Canino', label: 'Canino' },
  { value: 'Felino', label: 'Felino' },
  { value: 'Otro', label: 'Otro' },
];

const sexOptions = [
  { value: 'male', label: 'Macho' },
  { value: 'female', label: 'Hembra' },
  { value: 'unknown', label: 'No especificado' },
];

const sizeOptions = [
  { value: '', label: 'Seleccionar tamaño' },
  { value: 'xs', label: '0-10 kg' },
  { value: 'small', label: '10-20 kg' },
  { value: 'medium', label: '20-30 kg' },
  { value: 'large', label: '30-40 kg' },
  { value: 'xl', label: '+40 kg' },
];

const neuteredOptions = [
  { value: 'unknown', label: 'No especificado' },
  { value: 'yes', label: 'Sí' },
  { value: 'no', label: 'No' },
];

// Función para calcular edad desde fecha de nacimiento
function calculateAgeFromBirthDate(birthDate: string): { years: number; months: number } {
  const birth = new Date(birthDate);
  const today = new Date();
  let years = today.getFullYear() - birth.getFullYear();
  let months = today.getMonth() - birth.getMonth();

  if (months < 0) {
    years--;
    months += 12;
  }

  if (today.getDate() < birth.getDate()) {
    months--;
    if (months < 0) {
      years--;
      months += 12;
    }
  }

  return { years: Math.max(0, years), months: Math.max(0, months) };
}

// Función para calcular fecha de nacimiento desde edad
function calculateBirthDate(years?: number, months?: number): string | undefined {
  if (!years && !months) return undefined;
  const today = new Date();
  const birthDate = new Date(today);
  birthDate.setFullYear(birthDate.getFullYear() - (years || 0));
  birthDate.setMonth(birthDate.getMonth() - (months || 0));
  return birthDate.toISOString().split('T')[0];
}

export default function PatientForm({ patient, ownerId, onSuccess }: PatientFormProps) {
  const createPatient = useCreatePatient();
  const updatePatient = useUpdatePatient();

  // Calcular edad inicial si hay fecha de nacimiento
  const initialAge = patient?.birth_date ? calculateAgeFromBirthDate(patient.birth_date) : null;

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<PatientFormData>({
    resolver: zodResolver(patientSchema),
    defaultValues: patient
      ? {
          name: patient.name,
          species: patient.species,
          breed: patient.breed || '',
          color: patient.color || '',
          sex: patient.sex,
          size: patient.size || undefined,
          age_years: initialAge?.years || '',
          age_months: initialAge?.months || '',
          weight: patient.weight || '',
          microchip_number: patient.microchip_number || '',
          is_neutered: patient.is_neutered === true ? 'yes' : patient.is_neutered === false ? 'no' : 'unknown',
          notes: patient.notes || '',
          allergies: patient.allergies || '',
          deceased: patient.deceased ?? false,
          deceased_date: patient.deceased_date || '',
        }
      : {
          sex: 'unknown',
          species: 'Perro',
          is_neutered: 'unknown',
        },
  });

  const onSubmit = async (data: PatientFormData) => {
    try {
      const birthDate = calculateBirthDate(
        data.age_years ? Number(data.age_years) : undefined,
        data.age_months ? Number(data.age_months) : undefined
      );

      // Convertir is_neutered de string a boolean | null
      const isNeutered = data.is_neutered === 'yes' ? true : data.is_neutered === 'no' ? false : null;

      const cleanData = {
        name: data.name,
        species: data.species,
        sex: data.sex,
        size: data.size || undefined,
        owner_id: ownerId,
        breed: data.breed || undefined,
        color: data.color || undefined,
        birth_date: birthDate,
        weight: data.weight ? Number(data.weight) : undefined,
        microchip_number: data.microchip_number || undefined,
        is_neutered: isNeutered,
        notes: data.notes || undefined,
        allergies: data.allergies || undefined,
        // Defunción: al marcarla el backend deshabilita la mascota y deja de mandarle
        // recordatorios de vacunas y controles.
        deceased: data.deceased ?? false,
        deceased_date: data.deceased ? (data.deceased_date || undefined) : undefined,
      };

      if (patient) {
        await updatePatient.mutateAsync({ id: patient.id, data: cleanData });
      } else {
        await createPatient.mutateAsync(cleanData);
      }
      onSuccess();
    } catch (error) {
      console.error('Error saving patient:', error);
    }
  };

  const isDeceased = watch('deceased');
  const isLoading = createPatient.isPending || updatePatient.isPending;

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Input
          id="name"
          label="Nombre *"
          placeholder="Nombre de la mascota"
          {...register('name')}
          error={errors.name?.message}
        />
        <Select
          id="species"
          label="Especie *"
          options={speciesOptions}
          {...register('species')}
          error={errors.species?.message}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Input
          id="breed"
          label="Raza"
          placeholder="Ej: Labrador, Siamés..."
          {...register('breed')}
          error={errors.breed?.message}
        />
        <Input
          id="color"
          label="Color"
          placeholder="Ej: Negro, Blanco..."
          {...register('color')}
          error={errors.color?.message}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Select
          id="sex"
          label="Sexo"
          options={sexOptions}
          {...register('sex')}
          error={errors.sex?.message}
        />
        <Select
          id="is_neutered"
          label="Castrado/Esterilizado"
          options={neuteredOptions}
          {...register('is_neutered')}
        />
        <Select
          id="size"
          label="Tamaño"
          options={sizeOptions}
          {...register('size')}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Input
          id="age_years"
          type="number"
          min="0"
          label="Edad (años)"
          placeholder="0"
          {...register('age_years')}
        />
        <Input
          id="age_months"
          type="number"
          min="0"
          max="11"
          label="Meses"
          placeholder="0"
          {...register('age_months')}
        />
        <Input
          id="weight"
          type="number"
          step="0.1"
          label="Peso (kg)"
          placeholder="Ej: 5.5"
          {...register('weight')}
          error={errors.weight?.message}
        />
      </div>

      <Input
        id="microchip_number"
        label="Número de microchip"
        placeholder="Número de identificación"
        {...register('microchip_number')}
        error={errors.microchip_number?.message}
      />

      <div>
        <label htmlFor="allergies" className="block text-sm font-medium text-taupe-700 mb-1.5">
          Alergias
        </label>
        <textarea
          id="allergies"
          {...register('allergies')}
          rows={2}
          className="w-full px-4 py-3 border border-cream-300 rounded-xl focus:ring-2 focus:ring-taupe-400 focus:border-taupe-400 outline-none transition-all text-taupe-800 placeholder-taupe-400"
          placeholder="Alergias conocidas..."
        />
      </div>

      <div>
        <label htmlFor="notes" className="block text-sm font-medium text-taupe-700 mb-1.5">
          Notas
        </label>
        <textarea
          id="notes"
          {...register('notes')}
          rows={3}
          className="w-full px-4 py-3 border border-cream-300 rounded-xl focus:ring-2 focus:ring-taupe-400 focus:border-taupe-400 outline-none transition-all text-taupe-800 placeholder-taupe-400"
          placeholder="Observaciones adicionales..."
        />
      </div>

      {/* Defunción: solo al editar una mascota que ya existe */}
      {patient && (
        <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 space-y-3">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              {...register('deceased')}
              className="rounded border-gray-300 text-taupe-600"
            />
            <span className="text-sm font-medium text-taupe-800">Falleció</span>
          </label>
          <p className="text-xs text-taupe-500">
            La mascota queda deshabilitada y deja de recibir recordatorios de vacunas y
            controles. La ficha y su historia clínica se conservan.
          </p>
          {isDeceased && (
            <Input
              id="deceased_date"
              type="date"
              label="Fecha (opcional)"
              {...register('deceased_date')}
            />
          )}
        </div>
      )}

      <div className="flex justify-end gap-2 pt-4">
        <Button type="submit" loading={isLoading}>
          {patient ? 'Guardar Cambios' : 'Crear Mascota'}
        </Button>
      </div>
    </form>
  );
}
